import {
  codeBenchActions,
  gradeBenchPanelActions,
  objectUtils
} from '@codametrix/ui-common';
import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { Action } from 'redux';
import { ActivityFeed } from '../../../project_starburst';
import type { ActivityFilter } from '../../../project_starburst';
import { filterActivity, toActivityFeedItems } from './activityFeedItems';

import './activity-panel-body.scss';

// Activity tab body. Reads the activity fetched by coding-screen.tsx and reduces it (via
// `toActivityFeedItems`) into the token model the project_starburst `ActivityFeed` renders — so all
// list styling lives in project_starburst and shared-views is untouched. Source slice is mode-dependent:
//   coding  → `state.codeBench.activity`      (fetchActivity)
//   grading → `state.gradeBenchPanel.activity` (getGraderWorkItemActivity)
//
// The compose box below is new: it dispatches the existing `addUserActivityComment` thunk (coding:
// `codeBenchActions`, keyed on `processInstance`; grading: `gradeBenchPanelActions`, keyed on
// `sdCaseId`), which POSTs the comment then re-dispatches the activity fetch — identical to how
// `CaseNotesPanelBody` re-dispatches `fetchCoderNotes` after `addCoderNote`. `processInstance`/
// `sdCaseId` are threaded in as props (mirroring `CaseNotesPanelBody`'s `lookupParams`) because
// `state.codeBench.processInstance` is NOT reliably populated on this route (see codingScreenApi.ts).
type SliceWithActivity = {
  activity?: readonly any[];
};

export type ActivityPanelBodyProps = {
  mode?: 'coding' | 'grading';
  processInstance?: AmplifyAPI.ProcessInstance;
  sdCaseId?: string;
  /** The Activity-tab filter chip selection, relayed here from `coding-screen.tsx`'s
   *  `CodingScreenApi.onActivityFilterChange`. Defaults to `'all'`, matching
   *  `CaseNotesActivityPanel`'s own local default before a chip is ever clicked. */
  activityFilter?: ActivityFilter;
};

export const ActivityPanelBody: React.FC<ActivityPanelBodyProps> = ({
  mode = 'coding',
  processInstance,
  sdCaseId,
  activityFilter = 'all'
}) => {
  const activity = useSelector((state: CMx.ApplicationState) =>
    mode === 'grading'
      ? (state.gradeBenchPanel as SliceWithActivity | undefined)?.activity
      : (state.codeBench as SliceWithActivity | undefined)?.activity
  );

  const dispatch = useDispatch();
  const [commentContent, setCommentContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const isGrading = mode === 'grading';
  const canAdd =
    !objectUtils.isEmpty(commentContent) &&
    !isSubmitting &&
    (isGrading ? !!sdCaseId : !!processInstance);

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCommentContent(e.target.value);
  };

  // Guard against a double-submit (double-click, or the ⌘+Enter shortcut and the button both
  // firing for one logical action) — see CaseNotesPanelBody for the identical rationale.
  const addComment = async () => {
    if (!canAdd) {
      return;
    }
    setIsSubmitting(true);
    try {
      if (isGrading) {
        await (dispatch(
          gradeBenchPanelActions.addUserActivityComment({
            sdCaseId: sdCaseId as string,
            comment: commentContent
          }) as any as Action
        ) as unknown as Promise<unknown>);
      } else {
        await (dispatch(
          codeBenchActions.addUserActivityComment({
            processInstance: processInstance as AmplifyAPI.ProcessInstance,
            comment: commentContent
          }) as any as Action
        ) as unknown as Promise<unknown>);
      }
      setCommentContent('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleComposeKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      addComment();
    }
  };

  return (
    <div className="activity-panel-body" data-testid="activity-panel-body">
      <div className="ac-comment-compose">
        <textarea
          ref={textareaRef}
          className="ac-comment-compose__input"
          data-testid="add-comment-textarea"
          value={commentContent}
          placeholder="Add a comment for the team — visible to anyone reviewing this case…"
          onChange={handleCommentChange}
          onKeyDown={handleComposeKeyDown}
          disabled={isSubmitting}
        />
        <div className="ac-compose-row">
          <span className="ac-hint">⌘ + Enter to add</span>
          <button
            type="button"
            className="ac-comment-add-btn"
            onClick={addComment}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Adding…' : 'Add comment'}
          </button>
        </div>
      </div>
      <ActivityFeed
        items={toActivityFeedItems(filterActivity(activity, activityFilter))}
      />
    </div>
  );
};

ActivityPanelBody.displayName = 'ActivityPanelBody';
